import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  FunctionRouteInfo,
  PLATFORM_API_BASE,
  PlatformRoute,
  RouteDefaults,
  RouteKind,
  RouteStatus,
  SLUG_RE,
  STATUS_LABELS,
  downloadJson,
  platformRoutesApi,
} from '../../services/platformRoutesApi';

let defaultsCache: Promise<RouteDefaults> | null = null;

/** Kinds, model pools and fixed route names (admin only, fetched once per page load). */
export function useRouteDefaults(enabled = true): RouteDefaults | null {
  const { getIdToken } = useAuth();
  const [defaults, setDefaults] = useState<RouteDefaults | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    defaultsCache = defaultsCache || platformRoutesApi(getIdToken).defaults();
    defaultsCache
      .then(d => !cancelled && setDefaults(d))
      .catch(() => {
        defaultsCache = null;
      });
    return () => {
      cancelled = true;
    };
  }, [getIdToken, enabled]);
  return defaults;
}

export interface RouteDraft {
  slug: string;
  kind: RouteKind;
  status: RouteStatus;
}

/** Problem with a route name, or null when it's fine. */
export function slugProblem(slug: string, defaults: RouteDefaults | null): string | null {
  if (!SLUG_RE.test(slug)) return 'camelCase letters and digits, 3 to 48 long, starting lowercase (e.g. mealSwapIdeas)';
  if (defaults?.fixed_routes.some(f => f.toLowerCase() === slug.toLowerCase())) return 'That is a fixed platform route; pick another name';
  return null;
}

const TOGGLE_COLORS: Record<RouteStatus, 'standard' | 'warning' | 'success'> = { off: 'standard', testing: 'warning', live: 'success' };

const copy = (text: string) => navigator.clipboard?.writeText(text).catch(() => undefined);

export const StatusToggle: React.FC<{ value: RouteStatus; onChange: (s: RouteStatus) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
  <ToggleButtonGroup size="small" exclusive value={value} disabled={disabled} onChange={(_, v) => v && onChange(v)}>
    {(Object.keys(STATUS_LABELS) as RouteStatus[]).map(s => (
      <Tooltip key={s} title={STATUS_LABELS[s].help}>
        <ToggleButton value={s} color={TOGGLE_COLORS[s]}>
          {STATUS_LABELS[s].label}
        </ToggleButton>
      </Tooltip>
    ))}
  </ToggleButtonGroup>
);

/** Route name, kind and status, with what the kind's defaults will set. */
export const RouteSetupFields: React.FC<{ draft: RouteDraft; onChange: (d: RouteDraft) => void; defaults: RouteDefaults | null }> = ({
  draft,
  onChange,
  defaults,
}) => {
  const kind = defaults?.kinds.find(k => k.kind === draft.kind);
  const problem = draft.slug ? slugProblem(draft.slug, defaults) : null;
  return (
    <Stack spacing={2}>
      <TextField
        label="Route name"
        size="small"
        value={draft.slug}
        onChange={e => onChange({ ...draft, slug: e.target.value.trim() })}
        error={!!problem}
        helperText={problem || 'Customers call POST /napi/tools/<name>.'}
        InputProps={{ startAdornment: <InputAdornment position="start">/napi/tools/</InputAdornment> }}
      />
      <TextField select label="Kind" size="small" value={draft.kind} onChange={e => onChange({ ...draft, kind: e.target.value as RouteKind })}>
        {(defaults?.kinds || []).map(k => (
          <MenuItem key={k.kind} value={k.kind}>
            {k.kind[0].toUpperCase() + k.kind.slice(1)}: {k.description}
          </MenuItem>
        ))}
      </TextField>
      {kind && (
        <Typography variant="caption" color="text.secondary">
          Sets: the platform waits {kind.go_timeout_s}s; EasyAI time limit about {kind.time_limit_s}s (learned automatically), backup model on failure, 2 retries;
          models {kind.models.join(', ')} with {kind.default_model} as the default (any model with ?model=); self-improvement on. Reliability settings the
          function already has are kept.
        </Typography>
      )}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Status
        </Typography>
        <StatusToggle value={draft.status} onChange={status => onChange({ ...draft, status })} />
      </Box>
    </Stack>
  );
};

const parseIds = (text: string) =>
  text
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean);

const curlFor = (route: PlatformRoute) => {
  const body = route.docs.request_example || (route.input_types.includes('text') ? { text: 'two eggs and toast' } : { image: 'https://example.com/photo.jpg' });
  return `curl -X POST "${PLATFORM_API_BASE}${route.path}" \\\n  -H "Authorization: Bearer $PASSIO_ACCESS_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body)}'`;
};

/** A function's dynamic platform route: set it up, or change status, docs and testers. */
export const PlatformRouteCard: React.FC<{ functionId: string; onChanged?: (route: PlatformRoute | null) => void }> = ({ functionId, onChanged }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => platformRoutesApi(getIdToken), [getIdToken]);
  const defaults = useRouteDefaults();
  const [info, setInfo] = useState<FunctionRouteInfo | null>(null);
  const [draft, setDraft] = useState<RouteDraft | null>(null);
  const [docs, setDocs] = useState({ summary: '', description: '', example: '' });
  const [testers, setTesters] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const show = useCallback((route: PlatformRoute | null) => {
    setDocs({
      summary: route?.docs.summary || '',
      description: route?.docs.description || '',
      example: route?.docs.request_example ? JSON.stringify(route.docs.request_example, null, 2) : '',
    });
    setTesters((route?.testers || []).join(', '));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    setError(null);
    api
      .get(functionId)
      .then(d => {
        if (cancelled) return;
        setInfo(d);
        setDraft({ slug: d.route?.slug || d.suggested.slug, kind: (d.route?.kind as RouteKind) || d.suggested.kind, status: 'testing' });
        show(d.route);
      })
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      cancelled = true;
    };
  }, [api, functionId, show]);

  const act = async (name: string, fn: () => Promise<PlatformRoute | null | void>, done?: string) => {
    setBusy(name);
    setError(null);
    setNotice(null);
    try {
      const route = await fn();
      if (route !== undefined) {
        setInfo(i => (i ? { ...i, route } : i));
        show(route);
        onChanged?.(route);
      }
      if (done) setNotice(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  };

  if (!info || !draft) return error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={22} />;
  const route = info.route?.slug ? info.route : null;

  const messages = (
    <>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
    </>
  );

  if (!route) {
    const problem = slugProblem(draft.slug, defaults);
    return (
      <Stack spacing={2}>
        {!info.in_platform_account && (
          <Alert severity="warning">
            Dynamic routes must be functions in the platform account (the one the Passio platform calls EasyAI as). Create or copy the function there first.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          Serve this function as a Passio platform route. The platform picks it up within a minute; no platform release needed.
        </Typography>
        <RouteSetupFields draft={draft} onChange={setDraft} defaults={defaults} />
        {messages}
        <Box>
          <Button
            variant="contained"
            disabled={!!busy || !!problem || !info.in_platform_account}
            onClick={() =>
              act(
                'setup',
                async () => (await api.setup(functionId, { slug: draft.slug, kind: draft.kind, status: draft.status })).route,
                'Set up. The platform serves it within a minute.',
              )
            }
          >
            {busy === 'setup' ? <CircularProgress size={18} color="inherit" /> : 'Serve this route'}
          </Button>
        </Box>
      </Stack>
    );
  }

  let exampleError: string | null = null;
  let example: Record<string, unknown> | undefined;
  if (docs.example.trim()) {
    try {
      const parsed = JSON.parse(docs.example);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) example = parsed;
      else exampleError = 'Must be a JSON object';
    } catch {
      exampleError = 'Not valid JSON';
    }
  }
  const curl = curlFor(route);
  const testsUrl = `/dashboard/function-runs/${route.function_id}?tab=tests&add=library&customer_id=${encodeURIComponent(route.customer_id)}`;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>POST {route.path}</Typography>
        <Tooltip title="Copy URL">
          <IconButton size="small" onClick={() => copy(`${PLATFORM_API_BASE}${route.path}`)}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <StatusToggle
          value={route.status}
          disabled={!!busy}
          onChange={status => act('status', () => api.update(functionId, { status }), `${STATUS_LABELS[status].label}. The platform picks it up within a minute.`)}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {route.kind} kind · the platform waits {route.go_timeout_s ?? '–'}s · takes {route.input_types.filter(t => ['text', 'image', 'video', 'audio'].includes(t)).join(', ')} ·
        route key {route.route_key} · {route.function_name}
      </Typography>
      {messages}

      <Typography variant="subtitle2">API docs</Typography>
      <TextField label="Summary" size="small" value={docs.summary} onChange={e => setDocs(d => ({ ...d, summary: e.target.value }))} inputProps={{ maxLength: 200 }} />
      <TextField
        label="Description"
        size="small"
        multiline
        minRows={2}
        value={docs.description}
        onChange={e => setDocs(d => ({ ...d, description: e.target.value }))}
        inputProps={{ maxLength: 4000 }}
      />
      <TextField
        label="Request example (JSON)"
        size="small"
        multiline
        minRows={2}
        value={docs.example}
        onChange={e => setDocs(d => ({ ...d, example: e.target.value }))}
        error={!!exampleError}
        helperText={exampleError || 'Shown in the API reference, e.g. {"text": "two eggs and toast"}'}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
      />
      <Box>
        <Button
          size="small"
          variant="outlined"
          disabled={!!busy || !!exampleError}
          onClick={() =>
            act(
              'docs',
              () => api.update(functionId, { docs: { summary: docs.summary, description: docs.description, request_example: example } }),
              'Docs saved. Live routes show them in the API reference within a few minutes.',
            )
          }
        >
          {busy === 'docs' ? <CircularProgress size={16} /> : 'Save docs'}
        </Button>
      </Box>

      <Typography variant="subtitle2">Testers</Typography>
      <TextField
        label="Passio customer ids that may call it while Testing"
        size="small"
        value={testers}
        onChange={e => setTesters(e.target.value)}
        helperText={`Comma separated. Also allowed: ${route.global_testers.length} global tester${route.global_testers.length === 1 ? '' : 's'} (Platform Routes › Testers).`}
      />
      <Box>
        <Button size="small" variant="outlined" disabled={!!busy} onClick={() => act('testers', () => api.update(functionId, { testers: parseIds(testers) }), 'Testers saved.')}>
          {busy === 'testers' ? <CircularProgress size={16} /> : 'Save testers'}
        </Button>
      </Box>

      <Typography variant="subtitle2">Try it</Typography>
      <Box sx={{ position: 'relative', bgcolor: 'grey.100', borderRadius: 1, p: 1.5, pr: 5 }}>
        <Typography component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {curl}
        </Typography>
        <IconButton size="small" onClick={() => copy(curl)} sx={{ position: 'absolute', top: 4, right: 4 }}>
          <CopyIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" component={RouterLink} to={testsUrl}>
          Set up tests
        </Button>
        <Tooltip title="Function, response objects, reliability, learning, route and test cases, to import on another EasyAI environment (e.g. prod)">
          <Button
            size="small"
            variant="outlined"
            disabled={!!busy}
            onClick={() => act('export', async () => downloadJson(await api.exportBundle(functionId), `platform-route-${route.slug}.json`))}
          >
            Export bundle
          </Button>
        </Tooltip>
        <Tooltip title="Only needed to turn this into a dedicated fixed route in the platform repo (typed handler, hand-written docs)">
          <Button
            size="small"
            disabled={!!busy}
            onClick={() => act('prompt', async () => void (await copy((await api.prompt(functionId)).prompt)), 'Prompt copied.')}
          >
            Copy prompt for platform repo
          </Button>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Change the instructions, models or reliability from the function itself; every new version is served automatically.{' '}
        <Link component={RouterLink} to={`/dashboard/function-runs/${route.function_id}?tab=performance&customer_id=${encodeURIComponent(route.customer_id)}`}>
          Performance
        </Link>
      </Typography>
    </Stack>
  );
};

export const PlatformRouteDialog: React.FC<{
  open: boolean;
  functionId: string | null;
  title?: string;
  onClose: () => void;
  onChanged?: (route: PlatformRoute | null) => void;
}> = ({ open, functionId, title, onClose, onChanged }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>{title || 'Platform route'}</DialogTitle>
    <DialogContent>{functionId && <PlatformRouteCard functionId={functionId} onChanged={onChanged} />}</DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default PlatformRouteCard;
